/* eslint-disable quote-props */
import { AppLanguage } from './language';

type Dict = Record<string, string>;

const normalize = (value: string) => value.replace(/\s+/g, ' ').trim();


/**
 * Canonical CyberFit lore terminology for English UI.
 *
 * Older frontend/backend payloads used Betweener, Betweenworld, or "The Between"
 * for the proper noun Межмирье. Interworld is the canonical English name.
 * Ordinary English uses of "between" are intentionally not touched.
 */
const canonicalizeInterworldTerminology = (value: string): string => {
  const canonical = (match: string): string =>
    match === match.toUpperCase() ? 'INTERWORLD' : 'Interworld';

  return value
    .replace(/\b(?:the\s+)?betweener\b/gi, canonical)
    .replace(/\b(?:the\s+)?betweenworld\b/gi, canonical)
    .replace(/\bthe\s+between\b/gi, canonical);
};

const EXACT_RAW: Dict = {
  // Common
  'Язык': 'Language',
  'Русский': 'Russian',
  'Английский': 'English',
  'Закрыть': 'Close',
  'Меню': 'Menu',
  'Выйти': 'Sign out',
  'Уровень': 'Level',
  'Главная': 'Home',
  'О проекте': 'About',
  'Модули': 'Modules',
  'Мир': 'World',
  'Контакты': 'Contacts',
  'Персонажи': 'Characters',
  'Создать': 'Create',
  'Гильдия': 'Guild',
  'Тренировки': 'Training',
  'Тренировка': 'Training',
  'Функциональные тренировки': 'Functional training',
  'Функциональные': 'Functional',
  'Беговые тренировки': 'Running training',
  'Беговые': 'Running',
  'Пробежки': 'Runs',
  'Короткие тренировки': 'Short training',
  'Короткие': 'Short',
  'Когнитивные': 'Cognitive',
  'Имерсивные тренировки': 'Immersive training',
  'Имерсивные': 'Immersive',
  'БОС тренировки': 'BFB training',
  'БОС': 'BFB',
  'Дыхательные тренировки': 'Breathing training',
  'Дыхательные': 'Breathing',
  'Назад к тренировкам': 'Back to training',
  '+ Создать персонажа': '+ Create character',
  'Сначала выберите персонажа': 'Choose a character first',
  'Пока без данных из таблицы': 'No table data yet',
  'Здесь будет интерфейс для доктора (заглушка)': 'Doctor interface will be here (placeholder)',
  'Привет! Я пациент!': 'Hi! I am a patient!',
  'Мини-игра': 'Mini-game',
  'Беговая тренировка': 'Running training',
  'Когнитивная тренировка': 'Cognitive training',
  'Имерсивная тренировка': 'Immersive training',
  'БОС тренировка': 'BFB training',
  'Дыхательная тренировка': 'Breathing training',
  'Нет коротких тренировок за этот день': 'No short training sessions for this day',
  'Доступно функциональных тренировок:': 'Available functional training sessions:',
  'Прогрессия': 'Progression',
  'ПРОГРЕССИЯ': 'PROGRESSION',
  'Пн': 'Mon',
  'Ср': 'Wed',
  'Чт': 'Thu',
  'Пт': 'Fri',
  'Сб': 'Sat',
  'Фев': 'Feb',
  'Мар': 'Mar',
  'Апр': 'Apr',
  'Май': 'May',
  'Июн': 'Jun',
  'Авг': 'Aug',
  'Сен': 'Sep',
  'Окт': 'Oct',
  'Ноя': 'Nov',
  'Дек': 'Dec',
  'Общий': 'Overall',
  'Остэон': 'Osteon',
  'Тэрра': 'Terra',
  'Техника': 'Technique',
  'Эхо': 'Echo',
  'Арканы': 'Arcana',
  'Межмирье': 'Interworld',
  'Пульс': 'Pulse',
  'Дыхание': 'Breathing',
  'Ур.': 'Lvl.',
  'Класс': 'Class',
  'Раса': 'Race',
  'Пол': 'Gender',
  'Попытка': 'Attempt',
  'Энергетический хвост': 'Energy trail',
  'Стабилизаторы': 'Stabilizers',
  'Радар тепло-холодно': 'Hot-cold radar',
  'Сжатие зоны': 'Zone compression',
  'Бой': 'Combat',
  'Захват маяков': 'Beacon capture',
  'Выпады': 'Lunges',
  'Берпи-арена': 'Burpee arena',
  'Рывки': 'Dashes',
  'Комбо-бой': 'Combo fight',
  'Выберите уровень': 'Select level',
  'Статистика': 'Statistics',
  'Жесты': 'Gestures',
  'Жест': 'Gesture',
  'Медиана За первую дату': 'Median for the first date',
  'Таймлайн и диаграмма Исикавы': 'Timeline and Ishikawa diagram',
  'CyberFit: проводник, биосистема и личная история': 'CyberFit: guide, biosystem, and personal story',
  'CyberFit: сообщество, связь и путь в мир проекта': 'CyberFit: community, communication, and a path into the project world',
  'CyberFit: возможности, тренировки и игровые системы': 'CyberFit: features, training, and game systems',
  'За среднюю дату': 'For the middle date',
  'За последнюю дату': 'For the last date',

  // Personal account and module cards
  'Профиль проводника': 'Guide profile',
  'Провизии': 'Provisions',
  'Сканер Провизии': 'Provisions scanner',
  'Сбалансированный рацион — основа сил героя. Следите за балансом КБЖУ.': 'A balanced diet is the foundation of your hero’s strength. Track protein, fat, carbohydrate, and calorie balance.',
  'Посмотрите КБЖУ за период и проанализируйте вплоть до приема пищи': 'Review nutrition for a selected period and analyze it down to each meal',
  'Проверить': 'Check',
  'Посмотреть': 'View',
  'Анализ метаболизма': 'Metabolism analysis',
  'Чтобы открыть точный расчет метаболизма, ваш Профиль Героя должен быть заполнен на 100%. Система должна знать о вас всё, чтобы дать самый точный совет!': 'To unlock an accurate metabolism calculation, your hero profile must be 100% complete. The system needs the full picture to give the most precise advice.',
  'Беговая активность': 'Running activity',
  'Исследуйте реальный город, чтобы открыть тайны виртуального мира. Каждая пробежка отмечает новые локации на карте и даёт вашему персонажу уникальные бонусы': 'Explore the real city to uncover the secrets of the virtual world. Each run marks new map locations and gives your character unique bonuses.',
  'Мышечная активность': 'Muscle activity',
  'Muscle активность': 'Muscle activity',
  'Превратите энергию своих мышц в силу удара для героя. Наша 3D-модель покажет, какие группы мышц дают игровые преимущества прямо сейчас': 'Turn muscle energy into strike power for your hero. The 3D model shows which muscle groups provide game advantages right now.',
  'Превратите энергию своих muscles в силу удара для героя. Наша 3D-модель покажет, какие группы muscles дают игровые преимущества прямо сейчас': 'Turn muscle energy into strike power for your hero. The 3D model shows which muscle groups provide game advantages right now.',
  'Когнитивные тренировки': 'Cognitive training',
  'Каждая решённая головоломка и упражнение на память заряжают вашего героя интеллектуальной энергией': 'Each solved puzzle and memory exercise charges your hero with intellectual energy.',

  // Common domain UI
  'Персонажи не найдены': 'No characters found',
  'Уровни не найдены': 'No levels found',
  'Нет попыток': 'No attempts',
  'Попытки:': 'Attempts:',
  'Поражение': 'Defeat',
  'Сохранено': 'Saved',
  'Принять': 'Accept',
  'Отклонить': 'Decline',
  'Отправить': 'Send',
  'Удалить': 'Delete',
  'Применить': 'Apply',
  'Сначала': 'First',
  'Все': 'All',
  'все': 'all',
  'Минимум': 'Minimum',
  'Скорость': 'Speed',
  'Данные': 'Data',
  'Данные:': 'Data:',
  'Объяснение': 'Explanation',
  'Описание': 'Description',
  'Действия': 'Actions',
  'Уверенность': 'Confidence',
  'Уверенность:': 'Confidence:',
  'Референсы': 'References',
  'Факт': 'Fact',
  'Бег': 'Running',
  'Ходьба': 'Walking',
  'Прыжки': 'Jumps',
  'Приседания': 'Squats',
  'Медбол': 'Medicine ball',
  'Ноги': 'Legs',
  'Аэробных': 'Aerobic',
  'Анаэробных': 'Anaerobic',
  'Курсор соответствует текущему положению проигрывателя': 'The cursor matches the current player position',
  'Качество жестов': 'Gesture quality',
  'КАЧЕСТВО ЖЕСТОВ': 'GESTURE QUALITY',
  'Углы сустава': 'Joint angles',
  'Задействованные мышцы': 'Active muscles',
  'Профиль высоты': 'Altitude profile',
  'Интерактивная Яндекс Карта': 'Interactive Yandex Map',
  'Перетаскивайте для поворота · Колёсико для приближения': 'Drag to rotate · Use the wheel to zoom',


  // Nutrition/KBGU/week modules
  'Питание': 'Nutrition',
  'Белки': 'Protein',
  'Белок': 'Protein',
  'Жиры': 'Fats',
  'Углеводы': 'Carbohydrates',
  'Калории': 'Calories',
  'КБЖУ': 'Calories / Protein / Fats / Carbohydrates',
  'БЖУ': 'Protein / Fats / Carbohydrates',
  'Б / Ж / У': 'P / F / C',
  'Б/Ж/У': 'P/F/C',
  'ккал · Б': 'kcal · P',
  '· Б': '· P',
  '· Ж': '· F',
  '· У': '· C',
  'Б': 'P',
  'Ж': 'F',
  'У': 'C',
  'грамм': 'grams',
  'ккал': 'kcal',

  'примерно высота стола': 'about table height',
  'неспешная ходьба': 'easy walk',
  'быстрая ходьба': 'brisk walk',
  'лёгкий бег': 'light run',
  'интенсивный бег': 'intense run',
  'Нагрузка была почти полностью аэробной — хорошо для выносливости и жиросжигания.': 'The load was almost entirely aerobic — good for endurance and fat burning.',
  'Высокая анаэробная нагрузка — значительные усилия на ускорениях и подъёмах.': 'High anaerobic load — significant effort during accelerations and climbs.',
  'от общего': 'of total',

  'Вы выполнили норму на 92%': 'You met 92% of the target',
  'Энергетический баланс': 'Energy balance',
  'Структура макроэлементов': 'Macronutrient structure',
  'Качество еды': 'Food quality',
  'Микронутриенты': 'Micronutrients',
  'Микронутриентная плотность': 'Micronutrient density',
  'Ритм питания': 'Meal rhythm',
  'Зоны коррекции': 'Correction zones',

  'Всё тело': 'Whole body',
  'всё тело': 'whole body',
  'свободная поза': 'free pose',
  'неизвестно': 'unknown',
  'сгибание': 'flexion',
  'отведение': 'abduction',
  'ротация': 'rotation',
  'подъем': 'raise',
  'наклон вперед': 'forward bend',
  'наклон в стороны': 'side bend',
  'поворот': 'rotation',
  'Макро': 'Macros',
  'Качество': 'Quality',
  'Микро': 'Micro',
  'Ритм': 'Rhythm',
  'Коррекция': 'Correction',
  'Белок / лейцин': 'Protein / leucine',
  'Углеводы / клетчатка': 'Carbs / fiber',
  'Нет данных': 'No data',
  'Нет данных питания за период': 'No nutrition data for the period',
  'Добавьте приёмы пищи, чтобы построить причинную диаграмму недели.': 'Add meals to build the week causal diagram.',
  'Неделя пока пустая.': 'The week is empty yet.',
  'Нет данных для таймлайна': 'No timeline data',
  'Нет данных для диаграммы Исикавы': 'No Ishikawa diagram data',
  'Нажми на точки, чтобы открыть приёмы.': 'Click the points to open meals.',
  'Большая точка': 'Large dot',
  'Маленькая точка': 'Small dot',
  'Вторичные влияния включены': 'Secondary influences enabled',
  'Фильтр': 'Filter',
  'ИТОГ НЕДЕЛИ': 'WEEK SUMMARY',
  'макроструктура': 'macro structure',
  'энергия': 'energy',
  'микро': 'micro',
  'коррекция': 'correction',
  'белок': 'protein',
  'жиры': 'fats',
  'углеводы': 'carbs',
  'Нажми, чтобы открыть карточку': 'Click to open the card',
  'Приём влияет на выбранную ветку анализа.': 'The meal affects the selected analysis branch.',
  'Таймлайн питания': 'Nutrition timeline',
  'Модель Исикавы': 'Ishikawa model',
  'Итог недели': 'Week summary',
  'Причинная карта недели: большие точки — главные причины, маленькие — дополнительные влияния.': 'Weekly causal map: large dots are main causes, small dots are secondary influences.',
  'Показаны только главные точки': 'Only main points are shown',
  'Приём пищи': 'Meal',
  'До тренировки': 'Before workout',
  'Сохранить удачную часть': 'Keep the successful part',
  'Поздние плотные приёмы лучше переносить частично на обед или ранний ужин. На вечер оставить лёгкую белково-овощную часть.': 'Late heavy meals are better moved partly to lunch or an early dinner. Keep a lighter protein-and-vegetable portion for the evening.',
  'Посмотри самые крупные точки на ветке энергии: обычно достаточно уменьшить масло, соусы, сыр или размер одного плотного приёма.': 'Look at the largest points on the energy branch: usually it is enough to reduce oil, sauces, cheese, or the size of one dense meal.',
  'Это крупный энергетический вклад. Если он часто выбивает неделю, уменьшай масло, соусы, сыр или размер порции, а не всё блюдо целиком.': 'This is a large energy contribution. If it often throws off the week, reduce oil, sauces, cheese, or portion size rather than removing the whole dish.',
  'Приём хорошо поддерживает белок. Его можно использовать как удачный шаблон для дней, где белок проседает.': 'This meal supports protein well. It can be used as a good template for days when protein is low.',
  'Удалить этот приём пищи? Дневная аналитика будет пересобрана на бэке.': 'Delete this meal? Daily analytics will be rebuilt on the backend.',

  // KBGU / nutrition deep module
  'Начальная дата': 'Start date',
  'Конечная дата': 'End date',
  'Максимум 28 дней': 'Maximum 28 days',
  'применить': 'Apply',
  'Таймлайн': 'Timeline',
  'Исикава': 'Ishikawa',
  'Сбросить масштаб': 'Reset zoom',
  'Загружаю данные питания…': 'Loading nutrition data…',
  'Загружаю data питания…': 'Loading nutrition data…',
  'Загружаю данные недели...': 'Loading week data...',
  'Загружаю data недели...': 'Loading week data...',
  'Загружаю питание за неделю…': 'Loading weekly nutrition…',
  'Пока нет данных питания за выбранный период': 'No nutrition data for the selected period yet',
  'Пока no data питания за выбранный период': 'No nutrition data for the selected period yet',
  'Недельная Исикава построена': 'Weekly Ishikawa is ready',
  'Данные не загрузились, но страницу можно открыть повторно': 'Data failed to load, but you can reopen the page',
  'Не удалось загрузить недельную диаграмму Исикавы. Проверьте бэк-эндпоинт /api/food/ishikawa-week.': 'Failed to load the weekly Ishikawa diagram. Check the backend endpoint /api/food/ishikawa-week.',
  'Ваша неделя в деталях': 'Your week in detail',
  'Анализ питания': 'Nutrition analysis',
  'Цель: баланс КБЖУ': 'Goal: PFC and calorie balance',
  'Панели анализа недели': 'Weekly analysis panels',
  'Дни на основной линии, приёмы пищи поднимаются ветками вверх.': 'Days are on the main line; meals rise on branches above it.',
  'День стоит на основной линии. Чем выше точка, тем раньше приём пищи. Цвет показывает главную причинную ветку Исикавы.': 'The day is on the main line. The higher the dot, the earlier the meal. Color shows the main Ishikawa cause branch.',
  'Показаны все причины': 'All causes are shown',
  'Большая точка — главная причина приёма. Маленькая — дополнительное влияние.': 'Large dot — main meal cause. Small dot — secondary influence.',
  'Недельная картина питания': 'Weekly nutrition picture',
  'Нажми на точку, чтобы открыть подробности приёма.': 'Click a dot to open meal details.',
  'Макроструктура': 'Macro structure',
  'хорошо': 'good',
  'внимание': 'attention',
  'риск': 'risk',
  'нейтрально': 'neutral',
  'перекос': 'imbalance',
  'точек на диаграмме': 'points on the diagram',
  'сторона': 'side',
  'баланс': 'balance',
  'Поднять энергию без хаоса': 'Raise energy without chaos',
  'Добавь один устойчивый приём: крупа, картофель, цельнозерновой хлеб, бобовые или молочный продукт. Лучше раньше вечером, а не поздно ночью.': 'Add one stable meal: grains, potatoes, whole-grain bread, legumes, or a dairy product. Prefer earlier in the evening, not late at night.',
  'Снизить энергетические пики': 'Reduce energy spikes',
  'Добавить белковую опору': 'Add a protein anchor',
  'В 1–2 приёма недели добавь явный источник белка: яйца, рыбу, птицу, творог, бобовые или протеиновую часть блюда.': 'Add a clear protein source to 1–2 meals this week: eggs, fish, poultry, cottage cheese, legumes, or the protein part of a dish.',
  'Смягчить жировой перекос': 'Soften the fat imbalance',
  'Если жиры выше цели, проверь блюда с сыром, маслом, орехами, жирным соусом и жаркой. Это часто главный незаметный источник калорий.': 'If fats are above target, check dishes with cheese, oil, nuts, fatty sauce, or frying. This is often the main hidden calorie source.',
  'Поднять клетчатку': 'Increase fiber',
  'Добавь овощи, ягоды, зелень, цельнозерновой гарнир или бобовые. Это улучшит качество углеводов и насыщение.': 'Add vegetables, berries, greens, whole-grain sides, or legumes. This improves carbohydrate quality and satiety.',
  'Увеличить микро-плотность': 'Increase micronutrient density',
  'Добавь цветные овощи, зелень, рыбу, яйца, кисломолочные продукты, орехи или бобовые. Цель — больше нутриентов на 1000 ккал.': 'Add colorful vegetables, greens, fish, eggs, fermented dairy, nuts, or legumes. The goal is more nutrients per 1000 kcal.',
  'Разгрузить поздний вечер': 'Lighten the late evening',
  'Сохранять удачные шаблоны': 'Keep successful patterns',
  'Критичных перекосов не видно. Сохрани 2–3 удачных приёма недели как повторяемые шаблоны.': 'No critical imbalances are visible. Keep 2–3 successful meals from the week as repeatable templates.',
  'Карточка приёма пищи': 'Meal card',
  'Детали приёма пищи': 'Meal details',
  'Фото не найдено': 'Photo not found',
  'время не указано': 'time not specified',
  'не указан': 'not specified',
  'Главная ветка': 'Main branch',
  'Все влияния на диаграмме': 'All influences on the diagram',
  'Большая точка:': 'Large dot:',
  'Маленькая точка:': 'Small dot:',
  'Энергия из макроэлементов': 'Energy from macronutrients',
  'Energy из макроэлементов': 'Energy from macronutrients',
  'Рекомендации по этому приёму': 'Recommendations for this meal',
  'Рекомендации по thisму приёму': 'Recommendations for this meal',
  'Блюда внутри приёма': 'Dishes in the meal',
  'Источник белка': 'Protein source',
  'Профиль углеводов': 'Carbohydrate profile',
  'не определён': 'not defined',
  'Сделать приём легче': 'Make the meal lighter',
  'В похожий приём стоит добавить явный источник белка: яйца, рыбу, птицу, творог, бобовые или протеиновую часть блюда.': 'For a similar meal, add a clear protein source: eggs, fish, poultry, cottage cheese, legumes, or the protein part of a dish.',
  'Сохранить белковый шаблон': 'Keep the protein pattern',
  'Снизить жировую плотность': 'Reduce fat density',
  'Жиры дают большую часть энергии. Можно уменьшить масло, сыр, жирный соус или заменить часть ингредиентов на менее жирные.': 'Fats provide most of the energy. You can reduce oil, cheese, fatty sauce, or replace some ingredients with lower-fat options.',
  'Добавь овощи, ягоды, зелень, цельнозерновой гарнир или бобовые, чтобы углеводы стали более устойчивыми.': 'Add vegetables, berries, greens, whole-grain sides, or legumes so carbohydrates become more stable.',
  'Сдвинуть плотность раньше': 'Move density earlier',
  'Если это повторяется, перенеси часть калорий на обед или ранний ужин, чтобы вечер был легче.': 'If this repeats, move some calories to lunch or early dinner so the evening is lighter.',
  'Добавить нутриентную плотность': 'Add nutrient density',
  'Калорийность есть, но микро-вклад выглядит скромным. Добавь зелень, овощи, ягоды, рыбу, яйца, бобовые или кисломолочный продукт.': 'There are calories, but the micronutrient contribution looks modest. Add greens, vegetables, berries, fish, eggs, legumes, or fermented dairy.',
  'Критичных перекосов по этому приёму не видно. Используй его как ориентир, если он хорошо вписался в день.': 'No critical imbalances are visible for this meal. Use it as a reference if it fit the day well.',
  'Витамины': 'Vitamins',
  'Витаminы': 'Vitamins',
  'Витамины и микроэлементы': 'Vitamins and trace elements',
  'Витаminы и микроэлементы': 'Vitamins and trace elements',
  'Макроэлементы': 'Macrominerals',
  'Микроэлементы': 'Trace elements',
  'Фолат': 'Folate',
  'Натрий': 'Sodium',
  'Калий': 'Potassium',
  'Кальций': 'Calcium',
  'Магний': 'Magnesium',
  'Железо': 'Iron',
  'Медь': 'Copper',
  'Марганец': 'Manganese',
  'калории': 'calories',
  'качество еды': 'food quality',
  'ритм': 'rhythm',
  'макро': 'macros',
  'качество': 'quality',
  'Витамин D': 'Vitamin D',
  'Витамин E': 'Vitamin E',
  'для просмотра выберите период': 'select a period to view',

  'НЕДОБОР БЕЛКА И КЛЕТЧАТКИ': 'PROTEIN AND FIBER DEFICIT',
  'Недобор белка и клетчатки': 'Protein and fiber deficit',
  'недобор белка и клетчатки': 'protein and fiber deficit',
  'Главный эффект недели — слабая белковая и пищеволоконная база.': 'Main weekly effect — a weak protein and fiber base.',
  'Главный эффект недели - слабая белковая и пищеволоконная база.': 'Main weekly effect — a weak protein and fiber base.',
  'слабая белковая и пищеволоконная база': 'weak protein and fiber base',
  'Слабая белковая и пищеволоконная база.': 'Weak protein and fiber base.',
  'Заметный недобор энергии.': 'Noticeable energy deficit.',
  'Заметный недобор клетчатки.': 'Noticeable fiber deficit.',
  'Заметный недобор белка.': 'Noticeable protein deficit.',
  'Показывает, из чего собрана энергия недели.': 'Shows what the week’s energy is made of.',
  'Оценка разнообразия витаминов и минералов в рационе.': 'Assessment of vitamin and mineral diversity in the diet.',
  'Показывает распределение питания во времени.': 'Shows how meals are distributed over time.',
  'Показывает, какие сильные зоны и ближайшие изменения.': 'Shows strong zones and nearest changes.',
  'Это приёмы, которые сильнее всего стоит изменить в следующий раз.': 'These are the meals that are most worth changing next time.',
  'Это приемы, которые сильнее всего стоит изменить в следующий раз.': 'These are the meals that are most worth changing next time.',
  'Большая точка — главная причина, маленькая — вторичное влияние': 'Large dot — main cause, small dot — secondary influence',
  'Большая точка — главная причина, маленькая — вторичное влияние.': 'Large dot — main cause, small dot — secondary influence.',
  'Большая точка - главная причина, маленькая - вторичное влияние.': 'Large dot — main cause, small dot — secondary influence.',
  'Недобор': 'Deficit',
  'недобор': 'deficit',
  'белка': 'protein',
  'клетчатки': 'fiber',
  'пищеволоконная база': 'fiber base',
  'пищеволоконной базы': 'fiber base',
  'приёма': 'meal',
  'приема': 'meal',
  'питательных': 'nutritional',
  'витаминов': 'vitamins',
  'минералов': 'minerals',
  'рационе': 'diet',
  'Среднее за день': 'Daily average',
  'Лучше смотреть на плотность на 1000 ккал': 'Best viewed as density per 1000 kcal',
  'поздних приёмов': 'late meals',
  'поздних приемов': 'late meals',
  'ключевых точек': 'key points',
  'нутриентов': 'nutrients',
  'микроэлементов': 'micronutrients',

  // Metabolism
  'Левая страница': 'Left page',
  'Правая страница': 'Right page',
  'Общий метаболизм': 'Overall metabolism',
  'Мышечная динамика': 'Muscle dynamics',
  'вернуться назад': 'go back',
  'детализация': 'details',
  'Получено': 'Consumed',
  'Потрачено': 'Burned',
  'Баланс': 'Balance',
  'Мышцы': 'Muscles',
  'Мышечная динамика:': 'Muscle dynamics:',
  'Загрузка графиков ODE...': 'Loading ODE charts...',
  'Как читать правую страницу': 'How to read the right page',
  'Верхние графики слева показывают общий фон организма. Здесь —': 'The upper charts on the left show the overall body background. Here —',
  'отдельные мышцы: левая/правая сторона, локальная нагрузка,': 'individual muscles: left/right side, local load,',
  'повреждение, MPS, MPB и баланс восстановления.': 'damage, MPS, MPB, and recovery balance.',
  'В выбранном диапазоне /timeseries пока не содержит динамических': 'In the selected range, /timeseries does not yet contain dynamic',
  'данных по мышцам. Текущий snapshot из /muscles не используется': 'muscle data. The current /muscles snapshot is not used',
  'как временной ряд, чтобы не рисовать ложную прямую линию.': 'as a time series to avoid drawing a false flat line.',
  'Синтез мышечного белка. Рост значения — сигнал восстановления и': 'Muscle protein synthesis. Rising values signal recovery and',
  'построения ткани.': 'tissue building.',
  'Распад мышечного белка. Может расти при повреждении, усталости и': 'Muscle protein breakdown. May rise with damage, fatigue, and',
  'дефиците энергии.': 'energy deficit.',
  'Локальное микроповреждение после нагрузки. Чем выше, тем больше': 'Local microdamage after load. The higher it is, the more',
  'нужен отдых.': 'rest is needed.',
  'Механический стимул мышцы. Это источник последующего': 'Mechanical muscle stimulus. It is the source of later',
  'восстановления и адаптации.': 'recovery and adaptation.',
  'Анаболизм — восстановление и построение тканей. На графиках это': 'Anabolism means recovery and tissue building. In the charts this',
  'связано с достаточной энергией, белком, восстановлением и': 'is linked to sufficient energy, protein, recovery, and',
  'снижением повреждения.': 'reduced damage.',
  'Катаболизм — расход запасов и распад. Он растёт при дефиците': 'Catabolism means using reserves and breakdown. It rises with',
  'энергии, усталости, стрессе, повреждении мышц и нехватке сна.': 'energy deficit, fatigue, stress, muscle damage, and lack of sleep.',
  'Белки:': 'Protein:',
  'Углеводы:': 'Carbs:',
  'Жиры:': 'Fats:',
  'Анаболизм — восстановление и построение тканей. На графиках это связано с достаточной энергией, белком, восстановлением и снижением повреждения.': 'Anabolism means recovery and tissue building. In the charts it is linked to sufficient energy, protein, recovery, and reduced damage.',
  'Катаболизм — расход запасов и распад. Он растёт при дефиците энергии, усталости, стрессе, повреждении мышц и нехватке сна.': 'Catabolism means using reserves and breakdown. It rises with energy deficit, fatigue, stress, muscle damage, and lack of sleep.',
  'Верхние графики слева показывают общий фон организма. Здесь — отдельные мышцы: левая/правая сторона, локальная нагрузка, повреждение, MPS, MPB и баланс восстановления.': 'The upper charts on the left show the overall body background. Here you see individual muscles: left/right side, local load, damage, MPS, MPB, and recovery balance.',
  'В выбранном диапазоне /timeseries пока не содержит динамических данных по мышцам. Текущий snapshot из /muscles не используется как временной ряд, чтобы не рисовать ложную прямую линию.': 'In the selected range, /timeseries does not yet contain dynamic muscle data. The current /muscles snapshot is not used as a time series to avoid drawing a false flat line.',
  'Рост значения — сигнал восстановления и построения ткани.': 'Rising values signal recovery and tissue building.',
  'Может расти при повреждении, усталости и дефиците энергии.': 'May rise with damage, fatigue, and energy deficit.',
  'Локальное микроповреждение после нагрузки. Чем выше, тем больше нужен отдых.': 'Local microdamage after load. The higher it is, the more rest is needed.',
  'Механический стимул мышцы. Это источник последующего восстановления и адаптации.': 'Mechanical muscle stimulus. This is the source of later recovery and adaptation.',

  // Scene / AR / map / muscle dashboards
  'ЭНЕРГОЗАТРАТЫ': 'ENERGY EXPENDITURE',
  'Всего ккал': 'Total kcal',
  'На текущем моменте': 'At current moment',
  'Средний темп': 'Average pace',
  'Длительность': 'Duration',
  'Ср. мощность': 'Avg. power',
  'Накоплено:': 'Accumulated:',
  'Сила': 'Strength',
  'Ловкость': 'Agility',
  'Антропоморф': 'Anthropomorph',
  'Мехатроник': 'Mechatronic',
  'Мехатроники': 'Mechatronics',
  'Мужской': 'Male',
  'Женский': 'Female',
  'Мужчина': 'Male',
  'Женщина': 'Female',
  'Владелец': 'Owner',
  'Офицер': 'Officer',
  'Участник': 'Member',
  'Ваша роль:': 'Your role:',
  'Приглашение в гильдию': 'Guild invitation',
  'Состав гильдии': 'Guild members',
  'Имя персонажа': 'Character name',
  'Сообщение': 'Message',
  'Пригласить': 'Invite',
  'Исключить': 'Remove',
  'В офицеры': 'Make officer',
  'Реакция': 'Reaction',
  'Руки': 'Arms',
  'стрельба': 'shooting',
  'уклонение': 'dodge',
  'ходьба': 'walking',
  'спринт': 'sprint',
  'темп': 'pace',
  'погоня': 'chase',
  'преследователь': 'pursuer',
  'осколки': 'shards',
  'разломы': 'rifts',
  'стена_реакции': 'reaction wall',
  'прыжковая_сетка': 'jump grid',
  'опасная_зона': 'danger zone',
  'Парирование': 'Parry',
  'Прыжковая мини-игра': 'Jump mini-game',
  'Победа': 'Victory',
  'Co-op победа': 'Co-op victory',
  'Co-op поражение': 'Co-op defeat',
  'Отменено': 'Canceled',
  'Приглашение отклонено': 'Invitation declined',
  'Ошибка операции': 'Operation error',
  'Почти готово…': 'Almost ready…',
  'Подготавливаем короткую тренировку…': 'Preparing short training…',
  'Синхронизируем движения персонажа…': 'Synchronizing character movements…',
  'Синхронизируем кадры…': 'Synchronizing frames…',
  'Строим цветовые сегменты…': 'Building color segments…',
  '✅ Модель загружена': '✅ Model loaded',

  // Physical activity analogies
  'примерно как одна конфета': 'about one candy',
  'примерно как маленькое яблоко': 'about a small apple',
  'примерно как стакан сока': 'about a glass of juice',
  'примерно как стакан апельсинового сока': 'about a glass of orange juice',
  'примерно как банан': 'about a banana',
  'примерно как батончик': 'about an energy bar',
  'примерно как шоколадный батончик': 'about a chocolate bar',
  'примерно как порция фри': 'about a serving of fries',
  'примерно как порция картофеля фри': 'about a serving of fries',
  'примерно как кусок пиццы': 'about a slice of pizza',
  'примерно как бургер': 'about a burger',
  'примерно как плотный приём пищи': 'about a dense meal',
  'примерно как полноценный обед': 'about a full lunch',
  'примерно как тарелка пасты болоньезе': 'about a plate of bolognese pasta',
  'примерно как дневная норма взрослого человека': 'about an adult daily norm',
  'примерно как одна жевательная конфета': 'about one gummy candy',
  'примерно рост человека': 'about human height',
  'примерно один этаж': 'about one floor',
  'примерно два этажа': 'about two floors',
  'примерно как 4-этажный дом': 'about a 4-story building',
  'примерно как 6-этажный дом': 'about a 6-story building',
  'примерно как 9-этажный дом': 'about a 9-story building',
  'примерно как 12-этажный дом': 'about a 12-story building',
  'примерно как 16-этажный дом': 'about a 16-story building',
  'примерно как 25-этажное здание': 'about a 25-story building',
  'примерно как очень высокое здание': 'about a very tall building',
  'примерно как башня или высокий холм': 'about a tower or a high hill',
  'примерно как крупный холм': 'about a large hill',
  'примерно как небольшая гора': 'about a small mountain',
  'примерно как гора': 'about a mountain',
  'примерно как очень высокая гора': 'about a very high mountain',
  'очень медленная прогулка': 'very slow walk',
  'умеренный бег': 'moderate run',
  'Преимущественно аэробная нагрузка с умеренными анаэробными вспышками.': 'Mostly aerobic load with moderate anaerobic bursts.',
  'Смешанная нагрузка — организм активно использовал оба энергетических пути.': 'Mixed load — the body actively used both energy pathways.',


  'Левая сторона': 'Left side',
  'Правая сторона': 'Right side',
  'Мощность': 'Power',
  'Энергозатраты': 'Energy expenditure',
  'Н': 'N',
  'Вт': 'W',
  'Вт/кг': 'W/kg',
  'м': 'm',
  'Высота': 'Altitude',
  'Расстояние': 'Distance',
  'Расстояние (м)': 'Distance (m)',
  'Высота (м)': 'Altitude (m)',
  'Время': 'Time',
  'Движение': 'Movement',
  'Лестница': 'Stairs',
  'Лифт': 'Elevator',
  'ЛЕВАЯ': 'LEFT',
  'ПРАВАЯ': 'RIGHT',
  'ОЖИДАНИЕ': 'WAITING',
  'ДВИЖЕНИЕ': 'MOVEMENT',
  'ШАГ ВЛЕВО': 'STEP LEFT',
  'ШАГ ВПРАВО': 'STEP RIGHT',
  'ПРИСЕДАНИЕ': 'SQUAT',
  'В ПРИСЕДЕ': 'SQUAT HOLD',
  'ПОДЪЁМ': 'RISE',
  'УДАР / ВЫСТРЕЛ': 'HIT / SHOT',
  'ПОПАДАНИЕ': 'HIT TAKEN',
  'УКЛОНЕНИЕ': 'DODGE',
  'ПРЫЖОК': 'JUMP',
  'ВРАГ УХОДИТ': 'ENEMY DODGES',
  'ВРАГ УШЁЛ': 'ENEMY DODGED',
  'ПОБЕДА': 'VICTORY',
  'Стрельба': 'Shooting',
  'Уклонение': 'Dodge',
  'Рывки вперёд': 'Forward lunges',
  'Боковые уклонения': 'Side dodges',
  'Выстрелы': 'Shots',
  'Пауза': 'Pause',
  'Воспроизвести': 'Play',
  'Обзор': 'Overview',
  '3-е лицо': 'Third person',
  'Вверх': 'Up',
  'Вниз': 'Down',
  'Влево': 'Left',
  'Вправо': 'Right',
  'окно': 'window',
  'по мышцам': 'by muscles',
  'общий': 'overall',
  'руки': 'arms',
  'ноги': 'legs',
  'Макс': 'Max',
  'Медиана': 'Median',
  'Мин': 'Min',
  'Итераций': 'Iterations',

  // Profile / account / registration
  'Ограничения учитываются для безопасного входа в систему и персонализации нагрузки.': 'Limitations are considered for safe system entry and load personalization.',
  'Чем точнее данные, тем лучше подстраиваются игровые, дыхательные и аналитические сценарии.': 'The more accurate the data, the better game, breathing, and analytics scenarios adapt.',
  'Точно удалить гильдию? Это действие необратимо.': 'Delete the guild? This action cannot be undone.',
  'Описание отсутствует': 'No description',
  'описание отсутствует': 'no description',

  // Biomechanics labels
  'слева': 'left', 'справа': 'right', 'лев': 'left', 'прав': 'right', 'л': 'left', 'п': 'right',
  'сустав': 'joint', 'Сгибание кисти': 'Wrist flexion', 'Отведение кисти': 'Wrist abduction', 'Пронация кисти': 'Forearm pronation',
  'Сгибание запястно-пястного сустава': 'Carpometacarpal flexion',
  'Отведение большого пальца ПФ': 'Thumb MCP abduction', 'Сгибание большого пальца ПФ': 'Thumb MCP flexion',
  'Сгибание большого пальца П': 'Thumb PIP flexion', 'Сгибание большого пальца Д': 'Thumb DIP flexion',
  'Отведение указательного пальца ПФ': 'Index MCP abduction', 'Сгибание указательного пальца ПФ': 'Index MCP flexion',
  'Сгибание указательного пальца П': 'Index PIP flexion', 'Сгибание указательного пальца Д': 'Index DIP flexion',
  'Отведение среднего пальца ПФ': 'Middle MCP abduction', 'Сгибание среднего пальца ПФ': 'Middle MCP flexion',
  'Сгибание среднего пальца П': 'Middle PIP flexion', 'Сгибание среднего пальца Д': 'Middle DIP flexion',
  'Отведение безымянного пальца ПФ': 'Ring MCP abduction', 'Сгибание безымянного пальца ПФ': 'Ring MCP flexion',
  'Сгибание безымянного пальца П': 'Ring PIP flexion', 'Сгибание безымянного пальца Д': 'Ring DIP flexion',
  'Отведение мизинца ПФ': 'Little MCP abduction', 'Сгибание мизинца ПФ': 'Little MCP flexion',
  'Сгибание мизинца П': 'Little PIP flexion', 'Сгибание мизинца Д': 'Little DIP flexion',
  'Разгибатель указательного пальца': 'Extensor indicis', 'Разгибатель мизинца': 'Extensor digiti minimi',
  'Первая ладонная межкостная мышца': 'First palmar interosseous', 'Первая тыльная межкостная мышца (1)': 'First dorsal interosseous (1)',
  'Первая тыльная межкостная мышца (2)': 'First dorsal interosseous (2)', 'Третья ладонная межкостная мышца': 'Third palmar interosseous',
  'Третья тыльная межкостная мышца': 'Third dorsal interosseous', 'Четвертая тыльная межкостная мышца': 'Fourth dorsal interosseous',

  // Minerals / recovery
  'Селен': 'Selenium', 'Фосфор': 'Phosphorus', 'Цинк': 'Zinc', 'Сон': 'Sleep', 'Стресс': 'Stress',
  'Утомление': 'Fatigue', 'Управление нагрузкой': 'Load management',
  'Разные единицы': 'Different units', 'разные единицы': 'different units',
  'Мощность:': 'Power:',
  'Ккал за шаг:': 'Kcal per step:',

  'ЦЕНТР': 'CENTER',
  'Центр': 'Center',
  'Левый': 'Left',
  'Правый': 'Right',
  'ЛЕВОСТОРОННЯЯ': 'LEFT SIDE',
  'ПРАВОСТОРОННЯЯ': 'RIGHT SIDE',
  'Левосторонняя': 'Left side',
  'Правосторонняя': 'Right side',
  'Энергозатр.': 'Energy exp.',
  'Среднее': 'Average',
  'скрыть все': 'hide all',
  'сейчас': 'current',
  'кадр': 'frame',
  'кН': 'kN',
  'мл': 'ml',
  'кг': 'kg',

  // Scene loaders
  'Загружаем 3D модель…': 'Loading 3D model…',
  'Обрабатываем данные движения…': 'Processing motion data…',
  'Калибруем мышечные группы…': 'Calibrating muscle groups…',
  'Загружаем карту маршрута…': 'Loading route map…',
  'Обрабатываем GPS точки…': 'Processing GPS points…',
  'Вычисляем высотный профиль…': 'Calculating altitude profile…',
  'Загружаем AR сцену…': 'Loading AR scene…',
  'Настраиваем эффекты и события…': 'Configuring effects and events…',
  '3D Мышцы': '3D Muscles',
  'GPS Маршрут': 'GPS Route',
  'AR Тренировка': 'AR Workout',

  // Metabolism page
  'Выберите дату': 'Select date',
  'Выберите начальную дату': 'Select start date',
  'Выберите конечную дату': 'Select end date',
  'Токен авторизации не найден. Войдите заново.': 'Authorization token not found. Please sign in again.',
  'Не удалось получить user_id из JWT.': 'Could not get user_id from JWT.',
  'Не удалось загрузить данные метаболизма': 'Failed to load metabolism data',
  'Не удалось получить рекомендацию': 'Failed to get recommendation',
  'Не удалось получить ответ модели': 'Failed to get model response',
  'Положительный баланс поддерживает анаболические процессы: организму проще восстанавливаться и строить ткани.': 'A positive balance supports anabolic processes: it is easier for the body to recover and build tissue.',
  'Отрицательный баланс усиливает катаболическую сторону: организм активнее расходует запасы.': 'A negative balance increases the catabolic side: the body uses reserves more actively.',
  'Баланс близок к равновесию: анаболические и катаболические процессы примерно уравновешены.': 'The balance is close to equilibrium: anabolic and catabolic processes are roughly balanced.',
  'есть': 'available',
  'нет в /timeseries': 'not in /timeseries',
  'Белки/жиры': 'Protein/Fats',
  'Рекомендация дня': 'Recommendation of the day',
  'Обычный приоритет': 'Normal priority',
  'Высокий': 'High',
  'Средний': 'Medium',
  'Мягкий': 'Gentle',
  'Источник': 'Source',
  'Восстановление': 'Recovery',
  'Тайминг питания': 'Nutrition timing',
  'Белковая поддержка': 'Protein support',
  'Восполнение углеводов': 'Carb repletion',
  'Подпитка углеводами': 'Carb refuel',
  'Восстановление мышц': 'Muscle recovery',
  'Гидратация': 'Hydration',
  'Коррекция тренировки': 'Training adjustment',
  'Фокус дня': 'Daily focus',
  'Коррекция недели': 'Weekly adjustment',
  'После тренировки': 'Post-workout',
  'Дыхательное восстановление': 'Breathing recovery',
  'Дыхательная активация': 'Breathing activation',
  'Когнитивная нагрузка': 'Cognitive load',

  // Metabolism charts
  'Кумулятивная усвоенная энергия из еды по ODE. Может отличаться от верхней карточки «получено», где обычно показаны введённые калории еды до усвоения.': 'Cumulative absorbed food energy by ODE. It may differ from the top “received” card, which usually shows entered food calories before absorption.',
  'Кумулятивный полный расход ODE: RMR плюс активность и дополнительные компоненты. Может отличаться от верхней карточки «потрачено», если там учитываются только активные/мышечные ккал.': 'Cumulative full ODE expenditure: RMR plus activity and additional components. It may differ from the top “spent” card if that card only includes active/muscle kcal.',
  'Повреждение / усталость / восстановление': 'Damage / Fatigue / Recovery',
  'Нагрузка / активация / ккал': 'Load / Activation / kcal',
  'Показывает синтез белка, распад белка и чистый накопительный белковый баланс.': 'Shows protein synthesis, protein breakdown, and cumulative net protein balance.',
  'Левая': 'Left',
  'Правая': 'Right',

  'Средняя ягодичная': 'Gluteus medius',
  'Большая ягодичная': 'Gluteus maximus',
  'Двуглавая мышца бедра, длинная головка': 'Biceps femoris, long head',
  'Двуглавая мышца бедра, короткая головка': 'Biceps femoris, short head',
  'Портняжная мышца': 'Sartorius',
  'Большая приводящая': 'Adductor magnus',
  'Гребенчатая мышца': 'Pectineus',
  'Тонкая мышца': 'Gracilis',
  'Подвздошная мышца': 'Iliacus',
  'Поясничная мышца': 'Psoas major',
  'Четырёхглавая мышца бедра': 'Quadriceps femoris',
  'Близнецовая мышца': 'Gemellus',
  'Грушевидная мышца': 'Piriformis',
  'Прямая мышца бедра': 'Rectus femoris',
  'Широкая промежуточная мышца': 'Vastus intermedius',
  'Медиальная икроножная мышца': 'Medial gastrocnemius',
  'Камбаловидная мышца': 'Soleus',
  'Задняя большеберцовая мышца': 'Tibialis posterior',
  'Передняя большеберцовая мышца': 'Tibialis anterior',
  'Мышцы, выпрямляющие позвоночник': 'Erector spinae',
  'Внутренние косые мышцы': 'Internal obliques',
  'Внешние косые мышцы': 'External obliques',
  'Дельтовидная мышца': 'Deltoid',
  'Надостная мышца': 'Supraspinatus',
  'Подостная мышца': 'Infraspinatus',
  'Подлопаточная мышца': 'Subscapularis',
  'Малая круглая мышца': 'Teres minor',
  'Большая круглая мышца': 'Teres major',
  'Большая грудная мышца': 'Pectoralis major',
  'Широчайшая мышца спины': 'Latissimus dorsi',
  'Клювовидно-плечевая мышца': 'Coracobrachialis',
  'Трёхглавая мышца, длинная головка': 'Triceps, long head',
  'Трёхглавая мышца, латеральная головка': 'Triceps, lateral head',
  'Трёхглавая мышца, средняя головка': 'Triceps, medial head',
  'Локтевая мышца': 'Anconeus',
  'Двуглавая мышца, длинная головка': 'Biceps, long head',
  'Двуглавая мышца, короткая головка': 'Biceps, short head',
  'Плечевая мышца': 'Brachialis',
  'Плечелучевая мышца': 'Brachioradialis',
  'Круглый пронатор': 'Pronator teres',
  'Анаболический индекс': 'Anabolic index',
  'Катаболический индекс': 'Catabolic index',

  'Анаболический драйв': 'Anabolic drive',
  'Катаболический драйв': 'Catabolic drive',
  'Чистый анаболический индекс': 'Net anabolic index',
  'Индекс восстановления': 'Recovery index',
  'Готовность': 'Readiness',
  'Гликемия': 'Glycemia',
  'Базовая глюкоза': 'Baseline glucose',
  'Инсулин': 'Insulin',
  'Гликоген печени': 'Liver glycogen',
  'Гликоген мышц': 'Muscle glycogen',
  'Энергобаланс': 'Energy balance',
  'Прогноз баланса за сутки': 'Daily balance forecast',
  'RMR за прошедшее время': 'Elapsed RMR',
  'RMR за сутки': 'Daily RMR',
  'Потрачено с начала дня': 'Spent since day start',
  'Потрачено за день': 'Spent for day',
  'Баланс калорий': 'Calorie balance',
  'Клетчатка': 'Fiber',
  'Лейцин': 'Leucine',
  'Пищевые волокна': 'Dietary fiber',
  'Пищевая клетчатка': 'Dietary fiber',
  'НЕДОБОР КЛЕТЧАТКИ': 'FIBER DEFICIT',
  'НЕДОБОР БЕЛКА': 'PROTEIN DEFICIT',
  'НЕТ ДАННЫХ ПИТАНИЯ': 'NO NUTRITION DATA',
  'Нет данных питания': 'No nutrition data',
  'No data ПИТАНИЯ': 'NO NUTRITION DATA',
  'За выбранный период по готовых записей еды.': 'There are no completed food records for the selected period.',
  'За выбранный период нет готовых записей еды.': 'There are no completed food records for the selected period.',
  'За выбранный период нет записей еды.': 'There are no food records for the selected period.',
  'Неделя собрана достаточно ровно': 'Week is well balanced',
  'Week собрана достаточно ровно': 'Week is well balanced',
  'WEEK СОБРАНА ДОСТАТОЧНО РОВНО': 'WEEK IS WELL BALANCED',
  'СОБРАНА ДОСТАТОЧНО РОВНО': 'IS WELL BALANCED',
  'собрана достаточно ровно': 'is well balanced',
  'Ключевые причины не выглядят критичными, но детали видны на ветках.': 'Key causes do not look critical, but details are visible on the branches.',
  'Ключевые причины не выглядят критичными, но детали видны на ветках': 'Key causes do not look critical, but details are visible on the branches',
  'Мало клетчатки на фоне углеводов': 'Low fiber relative to carbohydrates',
  'Мало клетчатки на фоне углеводов.': 'Low fiber relative to carbohydrates.',
  'Высокий энергетический вклад': 'High energy contribution',
  'Сильный белковый вклад': 'Strong protein contribution',
  'Сильный жировой вклад': 'Strong fat contribution',
  'Сильный углеводный вклад': 'Strong carbohydrate contribution',
  'Приём заметно влияет на зону коррекции недели': 'This meal noticeably affects the week correction zone',
  'Прием заметно влияет на зону коррекции недели': 'This meal noticeably affects the week correction zone',
  'Meal заметно влияет на зону коррекции недели': 'This meal noticeably affects the week correction zone',
  'Оценивает разнообразие витаминов и минералов в рационе.': 'Evaluates the variety of vitamins and minerals in the diet.',
  'Большая точка = главная причина, маленькая = вторичное влияние': 'Large dot = main cause, small dot = secondary influence',
  'Найдена главная причина.': 'Main cause found.',
  'Завтрак': 'Breakfast',
  'Обед': 'Lunch',
  'Ужин': 'Dinner',
  'Перекус': 'Snack',
  'Завтрак/перекус': 'Breakfast/snack',
  'смешанный': 'mixed',
  'медленные': 'slow',
  'быстрые': 'fast',
  'в основном медленные': 'mostly slow',
  'в основном быстрые': 'mostly fast',
  'не определена': 'not defined',
  'не определено': 'not defined',
  'Мышечное повреждение': 'Muscle damage',
  'Симпатический тонус': 'Sympathetic tone',
  'Парасимпатический тонус': 'Parasympathetic tone',
  'Давление сна': 'Sleep pressure',
  'Воспаление': 'Inflammation',
  'Усвоение калорий': 'Calorie absorption',
  'Усвоение углеводов': 'Carb absorption',
  'Усвоение белков': 'Protein absorption',
  'Усвоение жиров': 'Fat absorption',
  'Усвоение лейцина': 'Leucine absorption',
  'Энергия получена': 'Energy in',
  'Энергия потрачена': 'Energy out',
  'Системный MPS': 'Systemic MPS',
  'Системный MPB': 'Systemic MPB',
  'Системный NPB': 'Systemic NPB',
  'Кортизол': 'Cortisol',
  'Не удалось загрузить персонажей': 'Failed to load characters',
  'Не удалось определить character_id: в JWT его нет, а список персонажей пуст.': 'Could not determine character_id: it is absent from JWT and the character list is empty.',
  'индекс': 'index',
  'г': 'g',
  'ммоль/л': 'mmol/L',
  'мг/дл': 'mg/dL',
  'ккал/ч': 'kcal/h',

  // Metabolism page full i18n pass
  'Начало периода': 'Period start',
  'Конец периода': 'Period end',
  'Выберите период не превышающий 28 дней': 'Select a period no longer than 28 days',
  'Системный метаболизм': 'Systemic metabolism',
  'Это общий фон организма: энергия, восстановление, стресс, питание и баланс анаболизма/катаболизма. Связанные показатели теперь идут на одной плоскости, а одинаковые alias-графики скрываются.': 'This is the body-wide background: energy, recovery, stress, nutrition, and the anabolic/catabolic balance. Related indicators are shown on one plane, and duplicate alias charts are hidden.',
  'Это общий фон организма: энергия, recovery, стресс, питание и баланс анаболизма/катаболизма. Связанные показатели теперь идут на одной плоскости, а одинаковые alias-charts скрываются.': 'This is the body-wide background: energy, recovery, stress, nutrition, and the anabolic/catabolic balance. Related indicators are shown on one plane, and duplicate alias charts are hidden.',
  'Поля модели:': 'Model fields:',
  'Точек:': 'Points:',
  'линии': 'lines',
  'Что показывает плоскость': 'What this plane shows',
  'Левая шкала:': 'Left scale:',
  '. Правая шкала:': '. Right scale:',
  'Left шкала:': 'Left scale:',
  '. Right шкала:': '. Right scale:',
  'не найдено в /timeseries': 'not found in /timeseries',
  'Сводные плоскости для системного вектора: синтез, распад и чистый баланс.': 'Summary planes for the systemic vector: synthesis, breakdown, and net balance.',
  'Сравнивает анаболический и катаболический индексы. Если линии расходятся, модель видит перекос в сторону восстановления или распада.': 'Compares anabolic and catabolic indices. If the lines diverge, the model sees a shift toward recovery or breakdown.',
  'Сырые стимулы модели до превращения в итоговые индексы.': 'Raw model stimuli before they are converted into final indices.',
  'Потоки ODE отделены от верхних карточек аналитики: карточки показывают фактическую еду/активность, а ODE показывает усвоение, базовый расход и баланс модели.': 'ODE flows are separated from the top analytics cards: the cards show actual food/activity, while ODE shows absorption, basal expenditure, and model balance.',
  'Кумулятивные потоки ODE: усвоенная энергия из еды и полный модельный расход. Это не то же самое, что верхние карточки: там еда считается как введённые калории, а расход часто показывает только активность/мышечные ккал.': 'Cumulative ODE flows: absorbed energy from food and total model expenditure. This is not the same as the top cards: there food is counted as entered calories, while expenditure often shows only activity/muscle kcal.',
  'Фактический баланс ODE с начала дня: усвоено минус полный расход модели на текущий момент. Старые alias-поля скрываются.': 'Actual ODE balance since the start of the day: absorbed minus total model expenditure at the current moment. Old alias fields are hidden.',
  'Отдельный прогнозный показатель. Он не смешивается с фактическим балансом, потому что при неизвестных событиях впереди легко вводит в заблуждение.': 'A separate forecast metric. It is not mixed with the actual balance because unknown future events can easily make it misleading.',
  'Скорость поступления калорий после еды. Это не накопленный баланс, а поток ккал/ч.': 'The rate of calorie inflow after eating. This is not accumulated balance but a kcal/h flow.',
  'Показывает реакцию на питание и доступность быстрого топлива. Единицы могут отличаться, поэтому важнее форма кривых.': 'Shows the response to nutrition and fast-fuel availability. Units may differ, so the curve shape matters more.',
  'Скорость усвоения углеводов, белков, жиров и лейцина после еды.': 'The absorption rate of carbohydrates, protein, fats, and leucine after eating.',
  'Суточное или накопленное поступление белков, жиров, углеводов, клетчатки и лейцина.': 'Daily or accumulated intake of protein, fats, carbohydrates, fiber, and leucine.',
  'Сопоставляет recovery/readiness с fatigue, damage и стрессовыми сигналами.': 'Compares recovery/readiness with fatigue, damage, and stress signals.',
  'Общий негативный фон: fatigue, stress, cortisol, inflammation и muscle damage.': 'Overall negative background: fatigue, stress, cortisol, inflammation, and muscle damage.',
  'Сырой анаболический стимул модели: питание, восстановление и нагрузка, которые поддерживают синтез.': 'Raw anabolic stimulus of the model: nutrition, recovery, and load that support synthesis.',
  'Сырой катаболический стимул: стресс, fatigue, damage и энергетический дефицит.': 'Raw catabolic stimulus: stress, fatigue, damage, and energy deficit.',
  'Стрессовая нагрузка на систему. Высокий стресс ухудшает восстановление.': 'Stress load on the system. High stress worsens recovery.',
  'Скорость усвоения нутриента после еды. Пик показывает, когда питание сильнее всего влияет на модель.': 'Nutrient absorption rate after eating. The peak shows when nutrition affects the model most strongly.',
  'Показатели ODE-модели. Смотри динамику: рост, спад и связь с тренировкой, питанием и восстановлением.': 'ODE model indicators. Look at dynamics: rise, fall, and links with training, nutrition, and recovery.',
  'Показатель локального состояния мышцы.': 'Local muscle status indicator.',
  'Широкий напрягатель фасции': 'Tensor fasciae latae',
  'MPS — Muscle Protein Synthesis. Это локальный синтез мышечного белка, то есть восстановительно-строительный процесс.': 'MPS — Muscle Protein Synthesis. This is local muscle protein synthesis, a recovery-and-building process.',
  'MPB — Muscle Protein Breakdown. Это локальный распад мышечного белка. Он может расти при сильной нагрузке, стрессе, дефиците энергии или плохом восстановлении.': 'MPB — Muscle Protein Breakdown. This is local muscle protein breakdown. It can increase with heavy load, stress, energy deficit, or poor recovery.',
  'NPB — Net Protein Balance. Это накопительный чистый белковый баланс мышцы. Положительный лучше для роста и восстановления, отрицательный указывает на дефицит.': 'NPB — Net Protein Balance. This is cumulative net protein balance of the muscle. Positive is better for growth and recovery; negative indicates deficit.',
  'Гликоген — быстрый энергетический запас мышцы. Чем ниже значение, тем меньше локального топлива для интенсивной работы.': 'Glycogen is a fast energy reserve of the muscle. The lower the value, the less local fuel for intense work.',
  'Энергия нагрузки — оценка энергетической стоимости работы конкретной мышцы.': 'Load energy is an estimate of the energy cost of work by a specific muscle.',
  'Сравнивает локальные сигналы восстановления и распада для выбранной мышцы.': 'Compares local recovery and breakdown signals for the selected muscle.',
  'Связывает локальное микроповреждение, усталость и восстановление на одной шкале.': 'Connects local microdamage, fatigue, and recovery on one scale.',
  'Показывает, как нагрузка и активация мышцы связаны с энергетической стоимостью работы.': 'Shows how muscle load and activation relate to the energy cost of work.',
  'Показывает локальные энергетические запасы мышцы, если backend отдаёт эти данные.': 'Shows local muscle energy stores if the backend returns this data.',
  'Общий фокус': 'General focus',
  'Осторожность': 'Caution',
  'Общее': 'General',
  'Рекомендация': 'Recommendation',
  'Ошибка аналитики': 'Analytics error',
  'Ошибка ODE timeseries': 'ODE timeseries error',
  'Ошибка рекомендательной системы': 'Recommendation system error',
  'Ошибка чата с моделью': 'Model chat error',
  'Ошибка объяснения метрики': 'Metric explanation error',
  'Не удалось загрузить мышцы ODE': 'Failed to load ODE muscles',


  // Metabolism visible screen cleanup, including mixed auto-generated EN strings
  'ODE-модель пока не вернула числовые параметры для графиков': 'The ODE model has not returned numeric chart parameters yet.',
  'ODE-модель пока не вернула числовые параметры для charts': 'The ODE model has not returned numeric chart parameters yet.',
  'Рекомендация и диалог с моделью': 'Recommendation and model dialogue',
  'Recommendation и диалог с моделью': 'Recommendation and model dialogue',
  'Рекомендация появится здесь': 'The recommendation will appear here',
  'Recommendation появится здесь': 'The recommendation will appear here',
  'Физиологический советник': 'Physiology advisor',
  'Уточнить': 'Ask a follow-up',
  'История короткая: показываем только диалог, без дубля полного ответа.': 'Short history: only the dialogue is shown, without duplicating the full answer.',
  'Нажми “дать рекомендацию” или задай вопрос. Полный ответ, действия, объяснение и references будут собраны в одну карточную ленту без дублей.': 'Click “get recommendation” or ask a question. The full answer, actions, explanation, and references will be collected into one card feed without duplicates.',
  'Нажми “дать рекомендацию” или задай вопрос. Полный ответ, действия, объяснение и референсы будут собраны в одну карточную ленту без дублей.': 'Click “get recommendation” or ask a question. The full answer, actions, explanation, and references will be collected into one card feed without duplicates.',
  'Сначала модель даёт итог по выбранному дню, затем можно уточнять: почему так, что делать, как читать charts, питание, тренировку и muscles.': 'The model first gives a summary for the selected day. Then you can ask follow-up questions: why this happened, what to do, how to read charts, nutrition, training, recovery, and muscles.',
  'Сначала модель даёт итог по выбранному дню, затем можно уточнять: почему так, что делать, как читать графики, питание, тренировку и мышцы.': 'The model first gives a summary for the selected day. Then you can ask follow-up questions: why this happened, what to do, how to read charts, nutrition, training, recovery, and muscles.',
  'Собираю ODE-состояние, историю и референсы...': 'Collecting ODE state, history, and references...',
  'Задай вопрос модели': 'Ask the model a question',
  'Например: “Почему низкая готовность?” или “Что сделать с энергобалансом сегодня?”': 'For example: “Why is readiness low?” or “What should I do with energy balance today?”',
  'Спроси о рекомендации, графике или конкретном показателе...': 'Ask about the recommendation, a chart, or a specific metric...',
  'спросить': 'ask',
  'думаю...': 'thinking...',
  'дать рекомендацию': 'get recommendation',
  'Дай рекомендацию по текущему состоянию метаболизма и мышц.': 'Give a recommendation for the current metabolism and muscle state.',
  'Дай рекомендацию по текущему состоянию метаболизма, энергии, восстановления и мышц.': 'Give a recommendation for the current state of metabolism, energy, recovery, and muscles.',
  'Что делать?': 'What should I do?',
  'Что мне лучше сделать сейчас с учётом энергии, восстановления и мышц?': 'What should I do now considering energy, recovery, and muscles?',
  'Почему так?': 'Why is that?',
  'Объясни, почему модель дала такую рекомендацию. Какие показатели повлияли сильнее всего?': 'Explain why the model gave this recommendation. Which metrics influenced it the most?',
  'Что скорректировать по питанию сегодня: калории, белки, углеводы и тайминг?': 'What should I adjust in nutrition today: calories, protein, carbohydrates, and timing?',
  'Стоит ли сегодня тренироваться? Если да, какую интенсивность выбрать?': 'Should I train today? If yes, what intensity should I choose?',
  'Что сделать для восстановления сегодня и какие риски видит модель?': 'What should I do for recovery today, and what risks does the model see?',
  'Какие мышцы сейчас требуют внимания и почему?': 'Which muscles need attention now and why?',
  'Ответ модели получен.': 'Model answer received.',
  'Рекомендация сформирована.': 'Recommendation created.',
  'Текущий ответ модели': 'Current model answer',
  'Что увидела модель': 'What the model found',
  'Ответ физиолога': 'Physiologist answer',
  'Не мед. диагноз': 'Not medical advice',
  'Модель:': 'Model:',
  'Режим': 'Mode',
  'Режим:': 'Mode:',
  'Общий вопрос': 'General question',
  'Объяснить метрику': 'Explain metric',
  'Объяснить тренд': 'Explain trend',
  'Безопасность': 'Safety',
  'Ты': 'You',
  'Модель': 'Model',

  // Metabolism chart groups, labels, and explanations
  'Анаболизм / катаболизм': 'Anabolism / catabolism',
  'Анаболизм vs катаболизм': 'Anabolism vs catabolism',
  'Анаболический / катаболический драйв': 'Anabolic / catabolic drive',
  'Энергия': 'Energy',
  'Усвоено / расход модели': 'Absorbed / model expenditure',
  'Энергобаланс сейчас': 'Current energy balance',
  'Прогноз баланса': 'Balance forecast',
  'RMR и расход': 'RMR and expenditure',
  'Усвоение энергии': 'Energy absorption',
  'Топливо': 'Fuel',
  'Глюкоза и инсулин': 'Glucose and insulin',
  'Гликоген': 'Glycogen',
  'Усвоение нутриентов': 'Nutrient absorption',
  'Макронутриенты': 'Macronutrients',
  'Стресс / восстановление': 'Stress / recovery',
  'Восстановление и готовность': 'Recovery and readiness',
  'Усталость / стресс / повреждение': 'Fatigue / stress / damage',
  'Симпатический / парасимпатический тонус': 'Sympathetic / parasympathetic tone',
  'Сон / гидратация': 'Sleep / hydration',
  'Синтез белка, распад белка и чистый белковый баланс на одной плоскости.': 'Protein synthesis, protein breakdown, and net protein balance on one plane.',
  'Базовый расход за прошедшее время и общий потраченный объём энергии. RMR за сутки показан отдельно и не должен сравниваться напрямую с расходом за текущий момент.': 'Basal expenditure over elapsed time and total energy spent. Daily RMR is shown separately and should not be compared directly with current elapsed expenditure.',
  'Глюкоза, инсулин, гликоген и усвоение нутриентов — фон для нагрузки и восстановления.': 'Glucose, insulin, glycogen, and nutrient absorption form the background for load and recovery.',
  'Запасы углеводов: печень, мышцы и общий гликоген, если модель их возвращает.': 'Carbohydrate stores: liver, muscles, and total glycogen if the model returns them.',
  'Высокие recovery/readiness обычно означают, что следующая нагрузка переносится легче.': 'High recovery/readiness usually means the next load is easier to tolerate.',
  'Баланс возбуждения и восстановления, если backend возвращает эти поля.': 'Balance of activation and recovery if the backend returns these fields.',
  'Вспомогательные системные факторы, влияющие на восстановление.': 'Additional systemic factors that influence recovery.',
  'Индекс смещения к восстановлению и построению тканей. Растёт при достаточной энергии, белке и хорошем восстановлении.': 'Index of shift toward recovery and tissue building. It rises with sufficient energy, protein, and good recovery.',
  'Индекс смещения к расходу и распаду. Может расти при дефиците энергии, усталости и повреждении.': 'Index of shift toward expenditure and breakdown. It can rise with energy deficit, fatigue, and damage.',
  'Чистый баланс между анаболическими и катаболическими сигналами. Выше нуля — перевес восстановления.': 'Net balance between anabolic and catabolic signals. Above zero means recovery dominates.',
  'Общее восстановление. Чем выше, тем легче организму переносить следующую нагрузку.': 'Overall recovery. The higher it is, the easier the body tolerates the next load.',
  'Готовность к активности: интегральная оценка восстановления, усталости и состояния.': 'Readiness for activity: an integrated estimate of recovery, fatigue, and state.',
  'Баланс энергии в килокалориях: получено минус потрачено с начала дня.': 'Energy balance in kilocalories: consumed minus burned since the start of the day.',
  'Прогноз баланса за сутки на основе текущего дня.': 'Daily balance forecast based on the current day.',
  'Глюкоза как быстрый источник энергии. Резкие изменения связаны с питанием и нагрузкой.': 'Glucose as a fast energy source. Sharp changes are linked to nutrition and load.',
  'Инсулиновый сигнал. В модели он помогает усвоению нутриентов и поддерживает анаболический режим.': 'Insulin signal. In the model it helps nutrient absorption and supports an anabolic mode.',
  'Запас углеводов, который используется как топливо для активности и восстановления.': 'Carbohydrate reserve used as fuel for activity and recovery.',
  'Общее мышечное повреждение после нагрузки. Чем выше, тем больше нужна пауза на восстановление.': 'Overall muscle damage after load. The higher it is, the longer the recovery pause needed.',
  'Утомление. Высокое значение может подавлять готовность и повышать катаболический фон.': 'Fatigue. A high value can suppress readiness and increase catabolic background.',
  'Катаболический стресс-сигнал. Рост кортизола обычно связан с нагрузкой, стрессом и недовосстановлением.': 'Catabolic stress signal. Cortisol growth is usually linked to load, stress, and under-recovery.',
  'Воспалительный фон после нагрузки или стресса. Рост может сопровождать повреждение и восстановление.': 'Inflammatory background after load or stress. Growth can accompany damage and recovery.',
  'Интегральный индекс модели. Его стоит читать по тренду, а не как абсолютную медицинскую величину.': 'Integrated model index. Read it by trend, not as an absolute medical value.',
  'единицы': 'units',
  'основные показатели': 'main metrics',
  'дополнительные показатели': 'additional metrics',
  'Точек': 'Points',
  'из': 'of',
  'будущий прогноз ограничен': 'future forecast limited',

  // Metabolism muscle chart text
  'Метаболизм мышц': 'Muscle metabolism',
  'Мышца • найдено:': 'Muscle • found:',
  'Плоскость графика': 'Chart plane',
  'Как читать график': 'How to read the chart',
  'Нет дублей': 'No duplicates',
  'Асимметрия': 'Asymmetry',
  'Прогноз': 'Forecast',
  'Будущие точки обрезаны до +': 'Future points are clipped to +',
  'ч, чтобы неизвестные события до конца дня не искажали картину.': 'h so unknown events later today do not distort the picture.',
  'Расхождение левой и правой линий показывает разницу нагрузки, повреждения или восстановления.': 'A difference between the left and right lines shows asymmetry in load, damage, or recovery.',
  'Расхождение левой и правой линий показывает разницу нагрузки, повреждения или recovery.': 'A difference between the left and right lines shows asymmetry in load, damage, or recovery.',
  'Если левая и правая стороны полностью совпадают, они схлопываются в одну линию “Обе”.': 'If the left and right sides fully match, they collapse into one “Both” line.',
  'Если left и right стороны полностью совпадают, они схлопываются в одну линию “Обе”.': 'If the left and right sides fully match, they collapse into one “Both” line.',
  'Обе': 'Both',
  'Обе:': 'Both:',
  'Левая:': 'Left:',
  'Правая:': 'Right:',
  'Выбери musclesу и логическую плоскость. Связанные показатели объединены на одном графике, а полностью совпадающие left/right линии показываются как одна линия “Обе”.': 'Choose a muscle and a metric plane. Related indicators are grouped on one chart, and identical left/right lines are shown as one “Both” line.',
  'Выбери мышцу и логическую плоскость. Связанные показатели объединены на одном графике, а полностью совпадающие левая/правая линии показываются как одна линия “Обе”.': 'Choose a muscle and a metric plane. Related indicators are grouped on one chart, and identical left/right lines are shown as one “Both” line.',
  'Для выбранной muscles no числовых значений по thisй группе показателей. Выбери другую плоскость или musclesу.': 'The selected muscle has no numeric values for this metric group. Choose another plane or muscle.',
  'Для выбранной мышцы нет числовых значений по этой группе показателей. Выбери другую плоскость или мышцу.': 'The selected muscle has no numeric values for this metric group. Choose another plane or muscle.',
  'В ODE-ряде пока no data по отдельным muscles. Проверь, что timeseries возвращает блок muscles с ключами left_* и right_*.': 'The ODE series has no separate muscle data yet. Check that timeseries returns a muscles block with left_* and right_* keys.',
  'В ODE-ряде пока нет данных по отдельным мышцам. Проверь, что timeseries возвращает блок muscles с ключами left_* и right_*.': 'The ODE series has no separate muscle data yet. Check that timeseries returns a muscles block with left_* and right_* keys.',
  'ODE-ряда пока no. Проверь, что запрос timeseries возвращает points[].': 'No ODE series points yet. Check that the timeseries request returns points[].',
  'ODE-ряда пока нет. Проверь, что запрос timeseries возвращает points[].': 'No ODE series points yet. Check that the timeseries request returns points[].',
  'Сигнал нагрузки': 'Load signal',
  'Микроповреждение': 'Microdamage',
  'Локальная усталость': 'Local fatigue',
  'Синтез мышечного белка': 'Muscle protein synthesis',
  'Распад мышечного белка': 'Muscle protein breakdown',
  'Чистый белковый баланс': 'Net protein balance',
  'Энергия нагрузки': 'Load energy',
  'Активация': 'Activation',
  'Сигнал нагрузки показывает, насколько сильно конкретная мышца была задействована в тренировке. Чем выше значение, тем сильнее локальный стимул.': 'Load signal shows how strongly a specific muscle was involved in training. The higher the value, the stronger the local stimulus.',
  'Микроповреждение отражает локальное повреждение мышечных волокон после нагрузки. Чем выше значение, тем больше времени нужно на восстановление.': 'Microdamage reflects local muscle fiber damage after load. The higher the value, the more recovery time is needed.',
  'Локальная усталость показывает, насколько мышца временно потеряла способность эффективно работать после нагрузки.': 'Local fatigue shows how much the muscle temporarily lost its ability to work effectively after load.',
  'Восстановление показывает, насколько мышца готова к новой нагрузке. Чем выше значение, тем лучше восстановление.': 'Recovery shows how ready the muscle is for a new load. The higher the value, the better the recovery.',
  'Анаболический индекс показывает смещение в сторону восстановления и построения ткани.': 'Anabolic index shows a shift toward recovery and tissue building.',
  'Катаболический индекс показывает смещение в сторону расхода и разрушения ткани.': 'Catabolic index shows a shift toward expenditure and tissue breakdown.',
  'Активация показывает степень вовлечения мышцы в движение. Значение около 1 означает высокую активацию.': 'Activation shows the degree of muscle involvement in movement. A value near 1 means high activation.',

  // Remaining KBGU meal detail dynamic strings
  'Тип приема пищи': 'Meal type',
  'Тип приёма пищи': 'Meal type',
  'Тип приема пищи: Breakfast, Тип приема пищи: Breakfast': 'Meal type: Breakfast',
  'Тип приёма пищи: Breakfast, Тип приёма пищи: Breakfast': 'Meal type: Breakfast',
  'Вклад в энергию недели': 'Weekly energy contribution',
  'Детали приема пищи': 'Meal details',
  'Влияет на распределение питания по времени': 'Affects meal distribution over time',
  'Влияет на распределение nutrition по времени': 'Affects meal distribution over time',
  'Небольшой вклад в общий рацион недели': 'Small contribution to the overall weekly diet',
  'Небольшой вклад в overall рацион недели': 'Small contribution to the overall weekly diet',

  // Metabolism coverage cleanup
  'Глюкоза': 'Glucose',
  'г/ч': 'g/h',
  'клювовидно_плечевая_мышца': 'coracobrachialis',
  'клювовидно-плечевая_мышца': 'coracobrachialis',
  'е': 'e',
  'лево': 'left',
  'право': 'right',
  'две': 'both',
  'оба': 'both',
  'обе': 'both',
  'двусторонняя': 'bilateral',
  '_л': '_left',
  '_п': '_right',
  '_л_': '_left_',
  '_п_': '_right_',
};

const EXACT: Dict = Object.fromEntries(Object.entries(EXACT_RAW).map(([key, value]) => [normalize(key), value]));

const PHRASES_RAW: Dict = {
  'КиберФит': 'CyberFit',
  'Остэон': 'Osteon',
  'Тэрра': 'Terra',
  'Межмирье': 'Interworld',
  'в межмирье': 'in Interworld',
  'Проводник': 'Guide',
  'проводник': 'guide',
  'Эфир': 'Ether',
  'эфирный': 'ether',
  'эфирная': 'ether',
  'ккал/мин': 'kcal/min',
  'ккал/ч': 'kcal/h',
  'ккал': 'kcal',

  'примерно высота стола': 'about table height',
  'примерно рост человека': 'about human height',
  'примерно один этаж': 'about one floor',
  'примерно два этажа': 'about two floors',
  'примерно как 4-этажный дом': 'about a 4-storey building',
  'примерно как 6-этажный дом': 'about a 6-storey building',
  'примерно как 9-этажный дом': 'about a 9-storey building',
  'примерно как 12-этажный дом': 'about a 12-storey building',
  'примерно как 16-этажный дом': 'about a 16-storey building',
  'примерно как 25-этажное здание': 'about a 25-storey building',
  'примерно как одна жевательная конфета': 'about one chewy candy',
  'примерно как одна конфета': 'about one candy',
  'примерно как маленькое яблоко': 'about a small apple',
  'примерно как стакан апельсинового сока': 'about a glass of orange juice',
  'примерно как стакан сока': 'about a glass of juice',
  'примерно как банан': 'about a banana',
  'примерно как шоколадный батончик': 'about a chocolate bar',
  'примерно как батончик': 'about an energy bar',
  'примерно как порция картофеля фри': 'about a portion of fries',
  'примерно как порция фри': 'about a portion of fries',
  'примерно как кусок пиццы': 'about a slice of pizza',
  'примерно как бургер': 'about a burger',
  'примерно как полноценный обед': 'about a full lunch',
  'примерно как плотный приём пищи': 'about a full meal',
  'примерно как тарелка пасты болоньезе': 'about a plate of bolognese pasta',
  'примерно как дневная норма взрослого человека': 'about an adult daily intake',
  'неспешная ходьба': 'easy walk',
  'быстрая ходьба': 'brisk walk',
  'лёгкий бег': 'light run',
  'интенсивный бег': 'intense run',
  'спринт': 'sprint',
  'Нагрузка была почти полностью аэробной — хорошо для выносливости и жиросжигания.': 'The load was almost entirely aerobic — good for endurance and fat burning.',
  'Высокая анаэробная нагрузка — значительные усилия на ускорениях и подъёмах.': 'High anaerobic load — significant effort during accelerations and climbs.',
  'от общего': 'of total',

  ' грамм': ' grams',
  'мин': 'min',
  'сек': 'sec',
  'Доступно функциональных тренировок': 'Available functional training sessions',
  'Функциональные': 'Functional',
  'Беговые': 'Running',
  'Короткие': 'Short',
  'Когнитивные': 'Cognitive',
  'Имерсивные': 'Immersive',
  'Дыхательные': 'Breathing',
  'Тренировки': 'Training',
  'тренировки': 'training',
  'тренировка': 'training',
  'Уровень': 'Level',
  'Ур.': 'Lvl.',
  'Тр.': 'Tr.',
  'Всего': 'Total',
  'Накоплено': 'Accumulated',
  'На текущем моменте': 'At current moment',
  'С начала дня': 'Since day start',
  'Потрачено': 'Burned',
  'Получено': 'Consumed',
  'Баланс': 'Balance',
  'Белки': 'Protein',
  'Белок': 'Protein',
  'Жиры': 'Fats',
  'Углеводы': 'Carbs',
  'Калории': 'Calories',
  'Мышцы': 'Muscles',
  'Мышечная': 'Muscle',
  'Левая': 'Left',
  'Правая': 'Right',
  'левая': 'left',
  'правая': 'right',
  'левый': 'left',
  'правый': 'right',
  'слева': 'left',
  'справа': 'right',
  'есть': 'available',
  'нет': 'no',
  'Нет данных': 'No data',
  'Загрузка': 'Loading',
  'Ошибка': 'Error',
  'Не удалось': 'Could not',
  'Показатели': 'Indicators',
  'это': 'this',
  'связано': 'is linked',
  'достаточной энергией': 'sufficient energy',
  'белком': 'protein',
  'снижением повреждения': 'reduced damage',
  'На графиках это': 'In the charts this',
  'На графиках': 'In the charts',
  'графики': 'charts',
  'графиков': 'charts',
  'графиках': 'charts',
  'динамика': 'dynamics',
  'страница': 'page',
  'данных': 'data',
  'данные': 'data',
  'по мышцам': 'by muscle',
  'мышцам': 'muscles',
  'мышцы': 'muscles',
  'мышц': 'muscles',
  'восстановления': 'recovery',
  'восстановление': 'recovery',
  'нагрузка': 'load',
  'локальная': 'local',
  'повреждение': 'damage',
  'баланс восстановления': 'recovery balance',
  'правую страницу': 'the right page',
  'Сначала выберите персонажа': 'Choose a character first',
  'Рекомендация': 'Recommendation',
  'Питание': 'Nutrition',
  'Приём пищи': 'Meal',
  'Приём': 'Meal',
  'Неделя': 'Week',
  'день': 'day',
  'за этот день': 'for this day',
  'Итог': 'Summary',
  'Энергия': 'Energy',
  'Качество еды': 'Food quality',
  'Ритм питания': 'Meal rhythm',
  'Зоны коррекции': 'Correction zones',

  'Всё тело': 'Whole body',
  'всё тело': 'whole body',
  'общий': 'overall',
  'руки': 'arms',
  'ноги': 'legs',
  'свободная поза': 'free pose',
  'неизвестно': 'unknown',
  'сгибание': 'flexion',
  'отведение': 'abduction',
  'ротация': 'rotation',
  'подъем': 'raise',
  'наклон вперед': 'forward bend',
  'наклон в стороны': 'side bend',
  'поворот': 'rotation',
};

const PHRASES = Object.entries(PHRASES_RAW).sort((a, b) => b[0].length - a[0].length);

const ANATOMY_RAW: Dict = {
  'средняя ягодичная мышца': 'gluteus medius',
  'двуглавая мышца бедра длинная г': 'biceps femoris long head',
  'двуглавая мышца бедра короткая г': 'biceps femoris short head',
  'портняжная мышца': 'sartorius',
  'большая приводящая мышца': 'adductor magnus',
  'широкий напрягатель фасции': 'tensor fasciae latae',
  'гребенчатая мышца': 'pectineus',
  'тонкая мышца': 'gracilis',
  'большая ягодичная мышца': 'gluteus maximus',
  'подвздошная мышца': 'iliacus',
  'поясничная мышца': 'psoas',
  'близнецовая мышца': 'gemellus',
  'грушевидная мышца': 'piriformis',
  'четырехглавая мышца бедра': 'quadriceps femoris',
  'прямая мышца бедра': 'rectus femoris',
  'широкая промежуточная мышца': 'vastus intermedius',
  'медиальная икроножная мышца': 'medial gastrocnemius',
  'камбаловидная мышца': 'soleus',
  'задняя большеберцовая мышца': 'tibialis posterior',
  'передняя большеберцовая мышца': 'tibialis anterior',
  'мышцы выпрямляющие позвоночник': 'erector spinae',
  'мышцы, выпрямляющие позвоночник': 'erector spinae',
  'внутренние косые мышцы': 'internal obliques',
  'внешние косые мышцы': 'external obliques',
  'дельтовидная мышца': 'deltoid',
  'надостная мышца': 'supraspinatus',
  'подостная мышца': 'infraspinatus',
  'подлопаточная мышца': 'subscapularis',
  'малая круглая мышца': 'teres minor',
  'большая круглая мышца': 'teres major',
  'большая грудная мышца': 'pectoralis major',
  'широчайшая мышца спины': 'latissimus dorsi',
  'клювовидно-плечевая мышца': 'coracobrachialis',
  'трехглавая мышца длинная г': 'triceps long head',
  'трехглавая мышца латеральная г': 'triceps lateral head',
  'трехглавая мышца средняя г': 'triceps medial head',
  'локтевая мышца': 'anconeus',
  'двуглавая мышца длинная головка': 'biceps long head',
  'двуглавая мышца длинная г': 'biceps long head',
  'двуглавая мышца короткая г': 'biceps short head',
  'плечевая мышца': 'brachialis',
  'плечелучевая мышца': 'brachioradialis',
  'мышца круглый пронатор': 'pronator teres',
  'тазобедренный сустав': 'hip joint',
  'коленный сустав': 'knee joint',
  'голеностопный сустав': 'ankle joint',
  'плечевой сустав': 'shoulder joint',
  'локтевой сустав': 'elbow joint',
  'лучезапястный сустав': 'wrist joint',
  'позвоночник': 'spine',
};

const ANATOMY = Object.entries(ANATOMY_RAW).sort((a, b) => b[0].length - a[0].length);

const MOTION_WORDS: Dict = {
  'сгибание': 'flexion',
  'отведение': 'abduction',
  'ротация': 'rotation',
  'наклон': 'tilt',
  'вперед': 'forward',
  'стороны': 'sideways',
  'поворот': 'rotation',
  'подъем': 'raise',
  'л': 'left',
  'п': 'right',
  'г': 'head',
  'мышца': 'muscle',
  'мышцы': 'muscles',
  'сустав': 'joint',
};

const hasCyrillic = (value: string) => /[А-Яа-яЁё]/.test(value);

const WORD_CHAR_CLASS = '0-9A-Za-zА-Яа-яЁё_';
const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Replace a configured phrase without matching it inside a larger word.
 *
 * The previous split/join fallback could translate `это` inside `этого`
 * and produce mixed-language UI such as `После thisго`. We intentionally
 * keep phrase matching case-sensitive, but require lexical boundaries when
 * the phrase starts/ends with a letter, digit or underscore.
 */
const replacePhrase = (value: string, from: string, to: string): string => {
  if (!from || !value.includes(from)) return value;

  const startsWithWordChar = new RegExp(`^[${WORD_CHAR_CLASS}]`).test(from);
  const endsWithWordChar = new RegExp(`[${WORD_CHAR_CLASS}]$`).test(from);
  const prefix = startsWithWordChar ? `(^|[^${WORD_CHAR_CLASS}])` : '';
  const suffix = endsWithWordChar ? `(?=$|[^${WORD_CHAR_CLASS}])` : '';
  const regex = new RegExp(`${prefix}${escapeRegExp(from)}${suffix}`, 'g');

  if (!startsWithWordChar) return value.replace(regex, to);
  return value.replace(regex, (_match, leadingBoundary: string) => `${leadingBoundary}${to}`);
};

const translateAnatomy = (rawValue: string): string | null => {
  const source = normalize(rawValue.replace(/_/g, ' ').replace(/,/g, ', ')).toLowerCase();
  if (!hasCyrillic(source)) return null;

  let side = '';
  let base = source;
  if (/(^| )(л)$/.test(base)) {
    side = 'left';
    base = base.replace(/(^| )л$/, '').trim();
  }
  if (/(^| )(п)$/.test(base)) {
    side = 'right';
    base = base.replace(/(^| )п$/, '').trim();
  }

  for (const [ru, en] of ANATOMY) {
    if (base === ru) return side ? `${side} ${en}` : en;
    if (base.startsWith(`${ru} `)) {
      const rest = base.slice(ru.length).trim();
      const translatedRest = rest
        .split(/\s+/)
        .map((part) => MOTION_WORDS[part] || part)
        .join(' ');
      return [side, en, translatedRest].filter(Boolean).join(' ');
    }
  }

  if (source.includes('_') || /сустав|мышц|мышца|позвоночник|ягодич|бедра|плеч/.test(source)) {
    return source
      .split(/\s+/)
      .map((part) => MOTION_WORDS[part] || part)
      .join(' ');
  }

  return null;
};

export const translateUiText = (value: unknown, language: AppLanguage): string => {
  const raw = String(value ?? '');
  const original = language === 'en' ? canonicalizeInterworldTerminology(raw) : raw;
  if (language !== 'en' || !hasCyrillic(original)) return original;

  const leading = original.match(/^\s*/)?.[0] ?? '';
  const trailing = original.match(/\s*$/)?.[0] ?? '';
  const trimmed = normalize(original);

  if (!trimmed) return original;

  const exact = EXACT[trimmed];
  if (exact) return `${leading}${exact}${trailing}`;

  const dynamicRules: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
    [/^Функциональные \((.+)\)$/i, (m) => `Functional (${m[1]})`],
    [/^Беговые \((.+)\)$/i, (m) => `Running (${m[1]})`],
    [/^Короткие \((.+)\)$/i, (m) => `Short (${m[1]})`],
    [/^Доступно функциональных тренировок:\s*(.+)$/i, (m) => `Available functional training sessions: ${m[1]}`],
    [/^(\d+(?:[.,]\d+)?)\s*Тр\.$/i, (m) => `${m[1]} tr.`],
    [/^Накоплено:\s*(.+?)\s*ккал$/i, (m) => `Accumulated: ${m[1]} kcal`],
    [/^На\s+(\d{1,2}:\d{2}(?::\d{2})?)$/i, (m) => `At ${m[1]}`],
    [/^окно\s+(\d+)с$/i, (m) => `window ${m[1]}s`],
    [/^Белки:\s*(.+)$/i, (m) => `Protein: ${m[1].replace(/\s*г/g, ' g')}`],
    [/^Углеводы:\s*(.+)$/i, (m) => `Carbs: ${m[1].replace(/\s*г/g, ' g')}`],
    [/^Жиры:\s*(.+)$/i, (m) => `Fats: ${m[1].replace(/\s*г/g, ' g')}`],
    [/^Точек ODE-ряда:\s*(.+)$/i, (m) => `ODE series points: ${m[1]}`],
    [/^Тип\s+при[ёе]ма\s+пищи:?\s*(.+)$/i, (m) => `Meal type: ${translateUiText(m[1].split(/[,;|]/)[0]?.replace(/^\s*(?:тип\s+при[ёе]ма\s+пищи|meal\s+type)\s*:?\s*/i, '').trim() || m[1], language)}`],
    [/^Детали\s+при[ёе]ма\s+пищи:?\s*(.*)$/i, (m) => `Meal details${m[1] ? `: ${translateUiText(m[1], language)}` : ''}`],
    [/^При[ёе]ма\s+пищи:?\s*(.+)$/i, (m) => `Meal: ${translateUiText(m[1], language)}`],
    [/^Влияет\s+на\s+распределение\s+(?:nutrition|питания)\s+по\s+времени\.?$/i, () => 'Affects meal distribution over time.'],
    [/^Небольшой\s+вклад\s+в\s+(?:overall|общий)\s+рацион\s+недели\.?$/i, () => 'Small contribution to the overall weekly diet.'],
    [/^(.+)\s+дают около\s+(\d+(?:[.,]\d+)?)%\s+энергии при[ёе]ма$/i, (m) => `${translateUiText(m[1], language)} provide about ${m[2]}% of meal energy`],
    [/^Найдено значимых микроэлементов:\s*(.+)$/i, (m) => `Significant micronutrients found: ${m[1]}`],
    [/^(?:Week|Неделя)\s+собрана\s+достаточно\s+ровно\.?$/i, () => 'Week is well balanced'],
    [/^WEEK\s+СОБРАНА\s+ДОСТАТОЧНО\s+РОВНО\.?$/i, () => 'WEEK IS WELL BALANCED'],
    [/^СОБРАНА\s+ДОСТАТОЧНО\s+РОВНО\.?$/i, () => 'IS WELL BALANCED'],
    [/^Ключевые\s+причины\s+не\s+выглядят\s+критичными,?\s+но\s+детали\s+видны\s+на\s+ветках\.?$/i, () => 'Key causes do not look critical, but details are visible on the branches.'],
    [/^Вклад\s+в\s+энергию\s+недели:?\s*(.+)$/i, (m) => `Weekly energy contribution: ${translateUiText(m[1], language).replace(/\s*ккал/gi, ' kcal')}`],
    [/^Высокий энергетический вклад:\s*(.+)$/i, (m) => `High energy contribution: ${m[1].replace(/\s*ккал/g, ' kcal')}`],
    [/^Сильный белковый вклад:\s*(.+)$/i, (m) => `Strong protein contribution: ${m[1].replace(/\s*г/g, ' g')}`],
    [/^Сильный жировой вклад:\s*(.+)$/i, (m) => `Strong fat contribution: ${m[1].replace(/\s*г/g, ' g')}`],
    [/^Сильный углеводный вклад:\s*(.+)$/i, (m) => `Strong carbohydrate contribution: ${m[1].replace(/\s*г/g, ' g')}`],
    [/^(?:Meal|При[ёе]м)\s+заметно влияет на зону коррекции недели\.?$/i, () => 'This meal noticeably affects the week correction zone.'],
    [/^Среднее за день:\s*(.+)$/i, (m) => `Daily average: ${translateUiText(m[1], language)}`],
    [/^Лейцин:\s*(.+)$/i, (m) => `Leucine: ${translateUiText(m[1], language)}`],
    [/^Large dot:\s*(.+)$/i, (m) => `Large dot: ${translateUiText(m[1], language)}`],
    [/^Small dot:\s*(.+)$/i, (m) => `Small dot: ${translateUiText(m[1], language)}`],
    [/^Большая точка:\s*(.+)$/i, (m) => `Large dot: ${translateUiText(m[1], language)}`],
    [/^Маленькая точка:\s*(.+)$/i, (m) => `Small dot: ${translateUiText(m[1], language)}`],
    [/^(.+?)\s*\/\s*(\d+(?:[.,]\d+)?)\s*g$/i, (m) => `${translateUiText(m[1], language)} / ${m[2]} g`],
    [/^(.+?)\s*\/\s*(\d+(?:[.,]\d+)?)\s*г$/i, (m) => `${translateUiText(m[1], language)} / ${m[2]} g`],
    [/^Total при[ёе]мов:\s*(.+)$/i, (m) => `Total meals: ${m[1]}`],
    [/^Всего при[ёе]мов:\s*(.+)$/i, (m) => `Total meals: ${m[1]}`],
    [/^(\d+)\s+поздних при[ёе]мов$/i, (m) => `${m[1]} late meals`],
    [/^(\d+)\s+ключевых точек$/i, (m) => `${m[1]} key points`],
    [/^(\d+)\s+нутриентов$/i, (m) => `${m[1]} nutrients`],
    [/^Клетчатка\s+(.+)$/i, (m) => `Fiber ${translateUiText(m[1], language)}`],
    [/^Белок\s+(.+)$/i, (m) => `Protein ${translateUiText(m[1], language)}`],
    [/^Жиры\s+(.+)$/i, (m) => `Fats ${translateUiText(m[1], language)}`],
    [/^Углеводы\s+(.+)$/i, (m) => `Carbs ${translateUiText(m[1], language)}`],
    [/^(\d+)\s*приёмов$/i, (m) => `${m[1]} meals`],
    [/^(Левая|Правая|Обе):\s*(.+)$/i, (m) => `${translateUiText(m[1], language)}: ${translateUiText(m[2], language)}`],
    [/^Время:\s*(.+)$/i, (m) => `Time: ${m[1]}`],
    [/^Не удалось загрузить персонажей:\s*(.+)$/i, (m) => `Failed to load characters: ${m[1]}`],
    [/^Ошибка аналитики:\s*(.+)$/i, (m) => `Analytics error: ${m[1]}`],
    [/^Ошибка ODE timeseries:\s*(.+)$/i, (m) => `ODE timeseries error: ${m[1]}`],
    [/^Не удалось загрузить мышцы ODE:\s*(.+)$/i, (m) => `Failed to load ODE muscles: ${m[1]}`],
    [/^Ошибка рекомендательной системы:\s*(.+)$/i, (m) => `Recommendation system error: ${m[1]}`],
    [/^Ошибка чата с моделью:\s*(.+)$/i, (m) => `Model chat error: ${m[1]}`],
    [/^Ошибка объяснения метрики:\s*(.+)$/i, (m) => `Metric explanation error: ${m[1]}`],
  ];

  for (const [regex, handler] of dynamicRules) {
    const match = trimmed.match(regex);
    if (match) return `${leading}${handler(match)}${trailing}`;
  }

  const anatomy = translateAnatomy(trimmed);
  if (anatomy && !hasCyrillic(anatomy)) return `${leading}${anatomy}${trailing}`;

  let translated = trimmed;
  for (const [from, to] of PHRASES) {
    translated = replacePhrase(translated, from, to);
  }

  translated = translated
    .replace(/НЕДОБОР БЕЛКА И КЛЕТЧАТКИ/g, 'PROTEIN AND FIBER DEFICIT')
    .replace(/WEEK\s+СОБРАНА\s+ДОСТАТОЧНО\s+РОВНО/gi, 'WEEK IS WELL BALANCED')
    .replace(/(?:Week|Неделя)\s+собрана\s+достаточно\s+ровно/gi, 'Week is well balanced')
    .replace(/СОБРАНА\s+ДОСТАТОЧНО\s+РОВНО/gi, 'IS WELL BALANCED')
    .replace(/собрана\s+достаточно\s+ровно/gi, 'is well balanced')
    .replace(/Ключевые\s+причины\s+не\s+выглядят\s+критичными,?\s+но\s+детали\s+видны\s+на\s+ветках/gi, 'Key causes do not look critical, but details are visible on the branches')
    .replace(/НЕДОБОР КЛЕТЧАТКИ/g, 'FIBER DEFICIT')
    .replace(/НЕТ ДАННЫХ ПИТАНИЯ/g, 'NO NUTRITION DATA')
    .replace(/ПИТАНИЯ/g, 'NUTRITION')
    .replace(/Детали\s+при[ёе]ма\s+пищи/gi, 'Meal details')
    .replace(/Тип\s+при[ёе]ма\s+пищи/gi, 'Meal type')
    .replace(/Влияет\s+на\s+распределение\s+(?:nutrition|питания)\s+по\s+времени/gi, 'Affects meal distribution over time')
    .replace(/Небольшой\s+вклад\s+в\s+(?:overall|общий)\s+рацион\s+недели/gi, 'Small contribution to the overall weekly diet')
    .replace(/Мало клетчатки на фоне углеводов/gi, 'Low fiber relative to carbohydrates')
    .replace(/Вклад\s+в\s+энергию\s+недели/gi, 'Weekly energy contribution')
    .replace(/Высокий энергетический вклад/gi, 'High energy contribution')
    .replace(/Сильный белковый вклад/gi, 'Strong protein contribution')
    .replace(/Сильный жировой вклад/gi, 'Strong fat contribution')
    .replace(/Сильный углеводный вклад/gi, 'Strong carbohydrate contribution')
    .replace(/(?:Meal|При[ёе]м) заметно влияет на зону коррекции недели/gi, 'This meal noticeably affects the week correction zone')
    .replace(/Оценивает разнообразие витаминов и минералов в рационе/gi, 'Evaluates the variety of vitamins and minerals in the diet')
    .replace(/Большая точка/gi, 'Large dot')
    .replace(/Маленькая точка/gi, 'Small dot')
    .replace(/главная причина/gi, 'main cause')
    .replace(/вторичное влияние/gi, 'secondary influence')
    .replace(/Завтрак/gi, 'Breakfast')
    .replace(/Обед/gi, 'Lunch')
    .replace(/Ужин/gi, 'Dinner')
    .replace(/Перекус/gi, 'Snack')
    .replace(/клетчатки/gi, 'fiber')
    .replace(/клетчатка/gi, 'fiber')
    .replace(/белка/gi, 'protein')
    .replace(/питания/gi, 'nutrition')
    .replace(/(\d[\d.,]*)\s*г/g, '$1 g')
    .replace(/(\d[\d.,]*)\s*м/g, '$1 m')
    .replace(/(\d[\d.,]*)\s*сек/g, '$1 sec')
    .replace(/(\d[\d.,]*)\s*мин/g, '$1 min');

  const afterAnatomy = translateAnatomy(translated);
  if (afterAnatomy && !hasCyrillic(afterAnatomy)) translated = afterAnatomy;

  return `${leading}${translated}${trailing}`;
};
